import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

function GenieGlyph({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="gx-g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#f4d57f" />
          <stop offset="100%" stopColor="#9a7a1d" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill="none" stroke="url(#gx-g)" strokeWidth="1" opacity="0.7" />
      <path
        d="M32 10 C 22 18, 18 28, 22 38 C 26 48, 38 48, 42 38 C 46 28, 42 18, 32 10 Z"
        fill="url(#gx-g)"
        opacity="0.9"
      />
      <circle cx="32" cy="32" r="2.4" fill="#0a0a0d" />
    </svg>
  );
}

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      if (u) {
        toast.success(`Welcome back, ${u.name || u.username}.`);
        const to = loc.state?.from || "/admin-dashboard";
        nav(to, { replace: true });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid credentials";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch" data-testid="admin-login-page">
      <div className="ax-grain" />
      {/* Left poster panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(700px 500px at 20% 20%, rgba(212,175,55,0.18), transparent 60%), radial-gradient(500px 300px at 80% 90%, rgba(212,175,55,0.08), transparent 60%), #0a0a0d",
          }}
        />
        <div className="relative z-10 m-16 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <GenieGlyph className="w-9 h-9" />
            <div>
              <div className="ax-gold-text font-semibold tracking-[0.32em] text-xs">GENIEX</div>
              <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
                Business&nbsp;OS
              </div>
            </div>
          </div>

          <div>
            <div className="ax-serif text-7xl leading-[0.95]" style={{ color: "var(--ax-text)" }}>
              Intelligence,
              <br />
              <span className="ax-gold-text">orchestrated.</span>
            </div>
            <p className="mt-6 max-w-md text-sm" style={{ color: "var(--ax-text-dim)" }}>
              A private command surface for the GenieX studio — leads, quotations, inventory and projects, in one
              cinematic workspace.
            </p>
            <div className="mt-10 flex gap-4 items-center">
              <div className="ax-chip ax-chip-gold">v1.0 · Studio</div>
              <div className="ax-chip">Internal access only</div>
            </div>
          </div>

          <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
            © GenieX — All operations protected.
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div className="w-full max-w-sm ax-fade-in">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <GenieGlyph className="w-8 h-8" />
            <div className="ax-gold-text font-semibold tracking-[0.32em] text-xs">GENIEX</div>
          </div>
          <h1 className="ax-serif text-4xl mb-2">Sign in</h1>
          <p className="text-sm mb-10" style={{ color: "var(--ax-text-dim)" }}>
            Authorised personnel only. All sessions are recorded.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="ax-label" htmlFor="ax-username">Username</label>
              <input
                id="ax-username"
                className="ax-input"
                placeholder="GenieXLogin"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                data-testid="admin-login-username"
              />
            </div>
            <div>
              <label className="ax-label" htmlFor="ax-password">Password</label>
              <input
                id="ax-password"
                type="password"
                className="ax-input"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="admin-login-password"
              />
            </div>

            {error && (
              <div
                className="text-xs px-3 py-2 rounded-md"
                style={{ background: "rgba(235,87,87,0.08)", color: "#f08e8e", border: "1px solid rgba(235,87,87,0.25)" }}
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="ax-btn ax-btn-gold w-full justify-center"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? "Authenticating…" : "Enter Business OS"}
            </button>
          </form>

          <div className="mt-10 ax-hairline" />
          <div className="mt-5 text-[10.5px] tracking-[0.18em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
            Secured by JWT · 12h session · 14d refresh
          </div>
        </div>
      </div>
    </div>
  );
}
